import type { CreatedShareLinkDtoOutput } from '@batiflow/api-client';
import { formatDateTime } from '@batiflow/i18n';
import { FormField, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Copy20Regular, Eye20Regular, EyeOff20Regular } from '@fluentui/react-icons';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useCreateShareLink, useSpacePolicy } from './api';
import { LinksList } from './links-list';
import {
  PASSWORD_MAX,
  PASSWORD_MIN,
  MAX_DOWNLOADS_LIMIT,
  buildRequest,
  createFailureText,
  expiryDate,
  initialDraft,
  limitsOf,
  type Draft,
  type DraftErrors,
  type ShareTarget,
  type SpaceLimits,
} from './model';
import { SHARE_LINK_MAX_DAYS } from '@batiflow/contracts';

const useStyles = __css({"surface":{"D305fb":"f1v8zv0x","Bns2gvi":"f1c209jx"},"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f13377fj"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"ltr":{"kgocjf":["f1r2v9sp","f1va4sj6"],"fsow6f":"fpgzoln"},"until":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef"},"linkRow":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"linkInput":{"Bh6795r":"fqerorx","Bgto7am":"fm46w4v"},"existing":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"note":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"}});

export interface ShareLinkDialogProps {
  spaceId: string;
  target: ShareTarget;
  /** Lists the item's links under the form (a folder has no panel to show them). */
  showExisting?: boolean;
  onClose: () => void;
}

/**
 * "Partager par lien" (GEDX-FE-03): the settings of a new link, then its address, shown once. The
 * settings the space forbids are disabled with the reason; the server still checks them all.
 */
export function ShareLinkDialog({
  spaceId,
  target,
  showExisting = false,
  onClose,
}: ShareLinkDialogProps) {
  const { t } = useTranslation('shareLinks');
  const space = useSpacePolicy(spaceId);

  let body;
  if (space.data) {
    const limits = limitsOf(space.data.policy);
    body = limits.linksOff ? (
      <MessageBar intent="warning" layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>{t('dialog.linksOff.title')}</MessageBarTitle>
          {t('dialog.linksOff.body')}
        </MessageBarBody>
      </MessageBar>
    ) : (
      <ShareForm
        spaceId={spaceId}
        target={target}
        limits={limits}
        showExisting={showExisting}
        onClose={onClose}
      />
    );
  } else if (space.isError) {
    body = (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>{t('dialog.policyFailed')}</MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void space.refetch();
            }}
          >
            {t('common:actions.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  } else {
    body = <SkeletonList count={3} />;
  }

  // The form carries its own buttons; the other states only close.
  if (space.data && !limitsOf(space.data.policy).linksOff) return body;
  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('dialog.title', { name: isolate(target.name) })}</DialogTitle>
          <DialogContent>{body}</DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t('common:actions.close')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface ShareFormProps {
  spaceId: string;
  target: ShareTarget;
  limits: SpaceLimits;
  showExisting: boolean;
  onClose: () => void;
}

function ShareForm({ spaceId, target, limits, showExisting, onClose }: ShareFormProps) {
  const { t } = useTranslation('shareLinks');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const locale = useLocale();
  const create = useCreateShareLink(spaceId);
  const key = useRef<{ payload: string; key: string } | null>(null);

  const [draft, setDraft] = useState<Draft>(() => initialDraft(limits));
  const [errors, setErrors] = useState<DraftErrors>({});
  const [revealed, setRevealed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedShareLinkDtoOutput | null>(null);

  const change = (patch: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };
  const until = expiryDate(draft.days, new Date());

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending) return;
    setFailure(null);
    const built = buildRequest(target, draft, limits);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    // One key per content: pressing again with the same settings reuses it.
    const payload = JSON.stringify(built.data);
    if (key.current?.payload !== payload) key.current = { payload, key: crypto.randomUUID() };
    try {
      const result = await create.mutateAsync({
        data: built.data,
        idempotencyKey: key.current.key,
      });
      // The password has done its job: it is not kept in the form.
      setDraft((current) => ({ ...current, password: '' }));
      setCreated(result);
    } catch (error) {
      setFailure(createFailureText(t, tCommon, error));
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !create.isPending) onClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        {created ? (
          <CreatedView
            title={t('dialog.title', { name: isolate(target.name) })}
            created={created}
            locale={locale}
            onClose={onClose}
          />
        ) : (
          <form
            noValidate
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <DialogBody>
              <DialogTitle>{t('dialog.title', { name: isolate(target.name) })}</DialogTitle>
              <DialogContent>
                <div className={styles.form}>
                  <p className={styles.intro}>
                    {t(target.type === 'folder' ? 'dialog.introFolder' : 'dialog.introDocument')}
                  </p>
                  {failure ? (
                    <MessageBar intent="error" layout="multiline" role="alert">
                      <MessageBarBody>
                        <MessageBarTitle>{t('dialog.failedTitle')}</MessageBarTitle>
                        {failure}
                      </MessageBarBody>
                    </MessageBar>
                  ) : null}

                  <FormField
                    label={t('dialog.label.label')}
                    hint={t('dialog.label.hint')}
                    error={errors.label ? t('dialog.label.invalid') : null}
                  >
                    <Input
                      maxLength={120}
                      value={draft.label}
                      onChange={(_event, data) => {
                        change({ label: data.value });
                      }}
                    />
                  </FormField>

                  <div>
                    <FormField
                      required
                      label={t('dialog.days.label')}
                      hint={t('dialog.days.hint', { max: SHARE_LINK_MAX_DAYS })}
                      error={
                        errors.days ? t('dialog.days.invalid', { max: SHARE_LINK_MAX_DAYS }) : null
                      }
                    >
                      <Input
                        type="number"
                        min={1}
                        max={SHARE_LINK_MAX_DAYS}
                        inputMode="numeric"
                        className={styles.ltr}
                        value={draft.days}
                        onChange={(_event, data) => {
                          change({ days: data.value });
                        }}
                      />
                    </FormField>
                    {until ? (
                      <p className={styles.until} role="status">
                        {t('dialog.days.until', { date: isolate(formatDateTime(until, locale)) })}
                      </p>
                    ) : null}
                  </div>

                  <FormField
                    label={t('dialog.password.label')}
                    hint={t('dialog.password.hint', { min: PASSWORD_MIN, max: PASSWORD_MAX })}
                    error={
                      errors.password
                        ? t('dialog.password.invalid', { min: PASSWORD_MIN, max: PASSWORD_MAX })
                        : null
                    }
                  >
                    <Input
                      type={revealed ? 'text' : 'password'}
                      autoComplete="new-password"
                      maxLength={PASSWORD_MAX}
                      className={styles.ltr}
                      value={draft.password}
                      onChange={(_event, data) => {
                        change({ password: data.value });
                      }}
                      contentAfter={
                        <Button
                          appearance="transparent"
                          size="small"
                          icon={revealed ? <EyeOff20Regular /> : <Eye20Regular />}
                          aria-label={t(revealed ? 'dialog.password.hide' : 'dialog.password.show')}
                          aria-pressed={revealed}
                          onClick={() => {
                            setRevealed((value) => !value);
                          }}
                        />
                      }
                    />
                  </FormField>

                  <div>
                    <Switch
                      label={t('dialog.watermark.label')}
                      checked={limits.watermarkRequired || draft.watermark}
                      disabled={limits.watermarkRequired}
                      onChange={(_event, data) => {
                        change({ watermark: data.checked });
                      }}
                    />
                    <p className={styles.note}>
                      {t(
                        limits.watermarkRequired
                          ? 'dialog.watermark.locked'
                          : 'dialog.watermark.hint',
                      )}
                    </p>
                  </div>

                  <div>
                    <Switch
                      label={t('dialog.download.label')}
                      checked={!limits.downloadsOff && draft.downloadAllowed}
                      disabled={limits.downloadsOff}
                      onChange={(_event, data) => {
                        change({ downloadAllowed: data.checked });
                      }}
                    />
                    <p className={styles.note}>
                      {t(limits.downloadsOff ? 'dialog.download.locked' : 'dialog.download.hint')}
                    </p>
                  </div>

                  {!limits.downloadsOff && draft.downloadAllowed ? (
                    <FormField
                      label={t('dialog.max.label')}
                      hint={t('dialog.max.hint')}
                      error={
                        errors.maxDownloads
                          ? t('dialog.max.invalid', { max: MAX_DOWNLOADS_LIMIT })
                          : null
                      }
                    >
                      <Input
                        type="number"
                        min={1}
                        max={MAX_DOWNLOADS_LIMIT}
                        inputMode="numeric"
                        className={styles.ltr}
                        value={draft.maxDownloads}
                        onChange={(_event, data) => {
                          change({ maxDownloads: data.value });
                        }}
                      />
                    </FormField>
                  ) : null}

                  {showExisting ? (
                    <section className={styles.existing} aria-labelledby="share-existing-title">
                      <h3 id="share-existing-title" className={styles.heading}>
                        {t('dialog.existing')}
                      </h3>
                      <LinksList
                        spaceId={spaceId}
                        target={target}
                        label={t('list.label')}
                        emptyText={t('list.emptyFolder')}
                      />
                    </section>
                  ) : null}
                </div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" disabled={create.isPending} onClick={onClose}>
                  {tCommon('actions.cancel')}
                </Button>
                <Button type="submit" appearance="primary" disabledFocusable={create.isPending}>
                  {create.isPending ? t('dialog.creating') : t('dialog.submit')}
                </Button>
              </DialogActions>
            </DialogBody>
          </form>
        )}
      </DialogSurface>
    </Dialog>
  );
}

interface CreatedViewProps {
  title: string;
  created: CreatedShareLinkDtoOutput;
  locale: ReturnType<typeof useLocale>;
  onClose: () => void;
}

type CopyState = 'idle' | 'copied' | 'failed';

/** The address of the new link, shown once, with "Copier le lien" and a fallback selection. */
function CreatedView({ title, created, locale, onClose }: CreatedViewProps) {
  const { t } = useTranslation('shareLinks');
  const styles = useStyles();
  const input = useRef<HTMLInputElement>(null);
  const actions = useRef<HTMLDivElement>(null);
  const [copy, setCopy] = useState<CopyState>('idle');

  // The address is what the person came for: focus goes to "Copier le lien".
  useEffect(() => {
    actions.current?.querySelector('button')?.focus();
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(created.url);
      setCopy('copied');
    } catch {
      // No clipboard (insecure origin, refused): select the address so Ctrl+C works.
      input.current?.focus();
      input.current?.select();
      setCopy('failed');
    }
  };

  return (
    <DialogBody>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={styles.form}>
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>{t('created.warningTitle')}</MessageBarTitle>
              {t('created.warning')}
            </MessageBarBody>
          </MessageBar>
          <FormField label={t('created.urlLabel')}>
            <Input
              readOnly
              className={`${styles.linkInput} ${styles.ltr}`}
              value={created.url}
              input={{ ref: input }}
              onFocus={(event) => {
                event.target.select();
              }}
            />
          </FormField>
          <div className={styles.linkRow} ref={actions}>
            <Button
              appearance="primary"
              icon={<Copy20Regular />}
              onClick={() => {
                void copyLink();
              }}
            >
              {t('created.copy')}
            </Button>
            <span role="status">
              {copy === 'copied' ? t('created.copied') : null}
              {copy === 'failed' ? t('created.copyFailed') : null}
            </span>
          </div>
          <p className={styles.note}>
            {t('created.until', {
              date: isolate(formatDateTime(created.link.expiresAt, locale)),
            })}
            {created.link.hasPassword ? ` ${t('created.passwordNote')}` : ''}
          </p>
        </div>
      </DialogContent>
      <DialogActions>
        <Button appearance="primary" onClick={onClose}>
          {t('created.done')}
        </Button>
      </DialogActions>
    </DialogBody>
  );
}


import "/opt/batiflow/app/apps/web/src/features/share-links/share-dialog.griffel.css";